import { useSidebar } from "@/components/ui";
import { useChat } from "@/contexts/ChatContext";
import { cn } from "@/lib/utils";
import { useThreadStore } from "@/store";
import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MessageError } from "./components/MessageError";
import { StreamingSkeletons } from "./components/MessageSkeletons";
import { PulsingLoader } from "./components/PulsingLoader";
import DeepResearchMessage from "./DeepResearchMessage";
import MessageItem from "./MessageItem";
import { sortMessagesByTimestamp } from "./utils/messageProcessing";
import { shouldShowInStreamingMessage } from "./utils/messageUtils";
import { parseStorageFiles } from "./utils/parsingUtils";
import { extractToolData } from "./utils/toolDataExtractors";

// Production Messages component with optimized streaming support

const BASE_CLASSES = {
  container: ["flex-1 relative h-full w-full", "pt-2"],
  wrapper: ["flex flex-col min-h-full w-full", "pb-2", "items-center"],
  list: "flex flex-col space-y-1 w-full max-w-4xl mx-auto transition-all duration-300 ease-in-out",
  anchor: "h-px w-full",
};

/**
 * Messages component - Production version with optimized streaming support
 *
 * Key improvements for image flickering and streaming:
 * - Stable file references stored in refs (not recalculated during streaming)
 * - File attachments isolated from message content updates
 * - Enhanced memoization with strict comparison
 * - Real-time streaming content updates
 */
const PureMessages = ({ id, className, messages = [] }) => {
  const setMessages = useThreadStore((state) => state.setMessages);
  const isStreaming = useThreadStore((state) => state.isStreaming);
  const currentToolCallName = useThreadStore(
    (state) => state.currentToolCallName
  );
  const location = useLocation();
  const isNewChatPath = location.pathname === "/chat/new";

  const scrollRef = useRef(null);

  // OPTIMISTIC RENDERING: Handlers for failed messages
  const handleRetryMessage = useCallback((messageId, threadId) => {
    const threadStore = useThreadStore.getState();
    threadStore.removeOptimisticMessage(threadId, messageId);
  }, []);

  const handleDeleteFailedMessage = useCallback((messageId, threadId) => {
    const threadStore = useThreadStore.getState();
    threadStore.removeOptimisticMessage(threadId, messageId);
  }, []);

  // Debug render tracking - track content changes for streaming messages
  const renderCountRef = useRef(0);
  const prevMessagesRef = useRef([]);

  useEffect(() => {
    renderCountRef.current += 1;

    // Check for content changes in streaming messages
    const streamingMessages =
      messages?.filter((m) => m?.isStreaming === true) || [];
    const prevStreamingMessages =
      prevMessagesRef.current?.filter((m) => m?.isStreaming === true) || [];

    streamingMessages.forEach((msg) => {
      const prev = prevMessagesRef.current?.find((m) => m?.id === msg?.id);
      if (prev && prev?.content !== msg?.content) {
        // No logging required.
      }
    });

    // Check for streaming completion
    const completedStreaming = prevStreamingMessages.filter(
      (prev) => !streamingMessages.find((m) => m?.id === prev?.id)
    );
    prevMessagesRef.current = messages || [];
  }, [messages, location.pathname]);

  const {
    isEditingMessage,
    editingMessageId,
    startEditingMessage,
    cancelEditingMessage,
    handleModelRerun,
  } = useChat();

  const { open } = useSidebar();

  // Sort messages by timestamp
  // CRITICAL: Ensure sortedMessages updates on every messages array change
  // For streaming, we want fresh sorted messages on every render to ensure Message components get updates
  const sortedMessages = useMemo(() => {
    if (!messages || messages.length === 0) return [];
    const sorted = sortMessagesByTimestamp(messages);

    // Log removed to reduce console noise.

    return sorted;
  }, [
    // Depend on messages array reference - always create new sorted array when messages change
    messages,
    // Also depend on content lengths to force recalculation during streaming
    messages
      ?.map(
        (m) =>
          `${m?.id}:${m?.content?.length || 0}:${m?.isStreaming ? "1" : "0"}`
      )
      .join("|"),
  ]);

  // CRITICAL FIX: Extract file attachments with stable references
  // Store in ref to prevent recreation during streaming updates
  const fileAttachmentsRef = useRef(new Map()); // messageId -> files array
  const fileAttachmentsLastUpdatedRef = useRef(new Map()); // messageId -> timestamp

  const stableFileAttachments = useMemo(() => {
    if (!sortedMessages || sortedMessages.length === 0) {
      fileAttachmentsRef.current.clear();
      return {};
    }

    const attachments = {};
    const now = Date.now();

    sortedMessages.forEach((message) => {
      if (!message?.id) return;

      // Don't render file attachments for assistant or tool messages
      if (message?.role === "assistant" || message?.role === "tool") return;

      // Don't render file attachments for messages with tool_calls
      const hasToolCalls =
        (message?.tool_calls && message.tool_calls.length > 0) ||
        (message.metadata?.tool_calls &&
          message.metadata.tool_calls.length > 0);

      if (hasToolCalls) return;

      // Extract files from message metadata using shared utility
      const files = parseStorageFiles(message.metadata);

      // CRITICAL: Only update file attachments if files actually changed
      // Compare with cached version to maintain stable references during streaming
      const cachedFiles = fileAttachmentsRef.current.get(message.id);
      const filesChanged =
        !cachedFiles ||
        cachedFiles.length !== files.length ||
        JSON.stringify(cachedFiles) !== JSON.stringify(files);

      if (files.length > 0) {
        if (filesChanged) {
          fileAttachmentsRef.current.set(message.id, files);
          fileAttachmentsLastUpdatedRef.current.set(message.id, now);
          attachments[message.id] = files;
        } else {
          attachments[message.id] = cachedFiles;
        }
      } else {
        // No files - remove from cache
        fileAttachmentsRef.current.delete(message.id);
        fileAttachmentsLastUpdatedRef.current.delete(message.id);
      }
    });

    // Clean up cache for messages that no longer exist
    const currentMessageIds = new Set(
      sortedMessages.map((m) => m?.id).filter(Boolean)
    );
    for (const [messageId] of fileAttachmentsRef.current) {
      if (!currentMessageIds.has(messageId)) {
        fileAttachmentsRef.current.delete(messageId);
        fileAttachmentsLastUpdatedRef.current.delete(messageId);
      }
    }

    return attachments;
  }, [
    // CRITICAL: Only depend on message IDs and file metadata, NOT on message content
    sortedMessages
      ?.map((m) => ({
        id: m?.id,
        storage_files: m?.metadata?.storage_files,
        role: m?.role,
        has_tool_calls: !!(m?.tool_calls || m?.metadata?.tool_calls),
      }))
      .join("|"),
  ]);

  // Extract tool data for all messages (cached to avoid redundant extraction)
  const messageToolDataMap = useMemo(() => {
    if (!sortedMessages || sortedMessages.length === 0) return {};

    const toolDataMap = {};
    sortedMessages.forEach((message) => {
      if (!message?.id) return;
      const isUser = message.role === "user";
      toolDataMap[message.id] = extractToolData(message, isUser);
    });
    return toolDataMap;
  }, [
    sortedMessages
      ?.map((m) => `${m?.id}:${m?.tool_calls?.length || 0}:${m?.metadata?.citations?.length || 0}`)
      .join("|"),
  ]);

  const handleMessageEditModeChange = useCallback(
    (messageId) => {
      if (!messageId) return;

      // Find the message to get its content
      const message = sortedMessages?.find((m) => m.id === messageId);
      if (!message) return;

      // Safety check: Only allow editing user messages
      if (message.role !== "user") {
        console.error("Cannot edit non-user message:", {
          messageId,
          role: message.role,
        });
        return;
      }

      startEditingMessage(messageId, message.content);
    },
    [startEditingMessage, sortedMessages]
  );

  const handleMessageRerun = useCallback(
    async (messageId) => {
      if (!messageId) return;

      // Find the message to get its content
      const message = sortedMessages?.find((m) => m.id === messageId);
      if (!message) return;

      // Safety check: Only allow rerunning user messages
      if (message.role !== "user") {
        console.error("Cannot rerun non-user message:", {
          messageId,
          role: message.role,
        });
        return;
      }

      try {
        await handleModelRerun(message.id, message.content);
      } catch (error) {
        console.error("Error in handleMessageRerun:", error);
      }
    },
    [handleModelRerun, sortedMessages]
  );

  if (isNewChatPath && (!sortedMessages || sortedMessages.length === 0)) {
    return (
      <div
        id={id}
        className={cn(BASE_CLASSES.container.join(" "), className)}
        data-path={location.pathname}
      >
        <div className={BASE_CLASSES.wrapper.join(" ")}>
          <div className={BASE_CLASSES.list}></div>
        </div>
      </div>
    );
  }

  return (
    <div id={id} className={cn(BASE_CLASSES.container.join(" "), className)}>
      <div className={BASE_CLASSES.wrapper.join(" ")}>
        <div className={BASE_CLASSES.list}>
          {sortedMessages?.length > 0 ? (
            <>
              {sortedMessages
                .filter((msg) => {
                  // Filter out follow-up content messages - they'll be merged with parent
                  if (
                    msg.metadata?.isFollowUpContent &&
                    msg.metadata?.toolCallMessageId
                  ) {
                    return false;
                  }
                  return true;
                })
                .map((message, index) => {
                  const stableMessageKey = `msg-${message.id}`;
                  const isUser = message?.role === "user";
                  const isLast = index === sortedMessages.length - 1;

                  // Find follow-up message for this message
                  const followUpMessage = sortedMessages.find(
                    (m) =>
                      m.metadata?.isFollowUpContent &&
                      m.metadata?.toolCallMessageId === message.id
                  );

                  // Merge content if there's a follow-up
                  const mergedMessage = followUpMessage
                    ? {
                        ...message,
                        content:
                          (message.content || "") +
                          (followUpMessage.content || ""),
                      }
                    : message;

                  const hasContent =
                    mergedMessage.content &&
                    mergedMessage.content.trim().length > 0;
                  const hasToolCalls =
                    mergedMessage?.tool_calls &&
                    Array.isArray(mergedMessage.tool_calls) &&
                    mergedMessage.tool_calls.length > 0;
                  // Check for file attachments from user uploads
                  const hasUserFileAttachments =
                    isUser &&
                    (!!stableFileAttachments[message.id] ||
                      message.metadata?.has_files === true ||
                      (message.metadata?.storage_files &&
                        message.metadata.storage_files !== "[]"));

                  // Use cached tool data instead of extracting again
                  const toolData = messageToolDataMap[message.id] || {};

                  // Only show FileAttachments for user messages
                  // AI-generated images are handled by DalleImageRenderer in MessageItem
                  const hasFileAttachments = hasUserFileAttachments;

                  // Get files to display (user uploads only)
                  const filesToDisplay = isUser
                    ? stableFileAttachments[message.id]
                    : null;

                  // Get cached tool data from previous message (for tool call messages)
                  // This allows the content message to show sources from the tool call message
                  const previousMessage =
                    index > 0 ? sortedMessages[index - 1] : null;
                  const previousMessageToolData = previousMessage
                    ? messageToolDataMap[previousMessage.id] || null
                    : null;

                  // Check if this is a reasoning response that should be shown even without content
                  const isReasoningResponse =
                    mergedMessage.metadata?.isReasoningResponse === true ||
                    mergedMessage.metadata?.inReasoningBlock === true;

                  // Check if this is a deep research message that should be shown even without content
                  const isDeepResearchMessage =
                    mergedMessage.metadata?.isDeepResearch === true ||
                    mergedMessage.metadata?.isDeepResearchInProgress === true;

                  if (
                    !hasContent &&
                    !hasToolCalls &&
                    !hasFileAttachments &&
                    !isReasoningResponse &&
                    !isDeepResearchMessage
                  ) {
                    return null;
                  }

                  return (
                    <div
                      key={stableMessageKey}
                      className={cn("flex flex-col w-full")}
                    >
                      <div
                        className={cn(
                          "flex w-full",
                          "max-w-4xl rounded-[calc(0.75rem-1px)] transition-all duration-300 ease-in-out",
                          {
                            "bg-bolt-elements-messages-background": true,
                          },
                          shouldShowInStreamingMessage(message) &&
                            "bg-gradient-to-b from-bolt-elements-messages-background from-30% to-transparent",
                          isUser && "pt-2"
                        )}
                      >
                        <div className="w-full">
                          {/* OPTIMISTIC RENDERING: Show error for failed messages */}
                          {message.isFailed ? (
                            <MessageError
                              messageId={message.id}
                              threadId={message.thread_id}
                              error={message.error}
                              onRetry={handleRetryMessage}
                              onDelete={handleDeleteFailedMessage}
                              className="mt-2"
                            />
                          ) : (
                            // Conditionally render DeepResearchMessage or MessageItem
                            (() => {
                              const isDeepResearchMessage =
                                mergedMessage.metadata?.isDeepResearch ||
                                mergedMessage.metadata
                                  ?.isDeepResearchFinalReport ||
                                mergedMessage.metadata?.deepResearchThreadId;

                              const MessageComponent = isDeepResearchMessage
                                ? DeepResearchMessage
                                : MessageItem;

                              return (
                                <MessageComponent
                                  message={mergedMessage}
                                  isStreaming={
                                    mergedMessage.isStreaming === true
                                  }
                                  isLastMessage={isLast}
                                  previousMessage={previousMessage}
                                  fileAttachments={filesToDisplay}
                                  toolData={toolData}
                                  onEdit={() => {
                                    handleMessageEditModeChange(
                                      mergedMessage.id
                                    );
                                  }}
                                  onRerun={() => {
                                    handleMessageRerun(mergedMessage.id);
                                  }}
                                  className={isUser ? "w-auto" : "w-full"}
                                />
                              );
                            })()
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

              {/* Show loading indicators when waiting for assistant response */}
              {(() => {
                const lastMessage =
                  sortedMessages?.length > 0
                    ? sortedMessages[sortedMessages.length - 1]
                    : null;
                const isLastMessageOptimisticUser =
                  lastMessage?.isOptimistic && lastMessage?.role === "user";

                // Check if there's an assistant message with actual content (including reasoning content)
                const hasReasoningContent =
                  lastMessage?.metadata?.reasoningContent ||
                  lastMessage?.metadata?.reasoning_content ||
                  lastMessage?.reasoningContent;
                const isReasoningMessageInitialized =
                  lastMessage?.metadata?.isReasoningResponse === true ||
                  lastMessage?.metadata?.inReasoningBlock === true;
                const isDeepResearchMessageInitialized =
                  lastMessage?.metadata?.isDeepResearch === true ||
                  lastMessage?.metadata?.isDeepResearchInProgress === true;
                const hasAssistantMessageWithContent =
                  lastMessage?.role === "assistant" &&
                  ((lastMessage?.content &&
                    lastMessage.content.trim().length > 0) ||
                    (hasReasoningContent &&
                      hasReasoningContent.trim().length > 0) ||
                    isReasoningMessageInitialized ||
                    isDeepResearchMessageInitialized);

                // Determine if we should show any loading indicator
                const shouldShowLoader =
                  !hasAssistantMessageWithContent &&
                  (isStreaming || isLastMessageOptimisticUser);

                if (!shouldShowLoader) return null;

                // If a tool is active, show StreamingSkeletons (tool-specific loading)
                // Otherwise show PulsingLoader (generic loading)
                if (currentToolCallName) {
                  return (
                    <StreamingSkeletons
                      isLastMessage={true}
                      isUserMessage={false}
                    />
                  );
                }

                // Show generic pulsing loader when no tool is active
                return (
                  <div
                    className={cn(
                      "transition-all duration-300 ease-in-out",
                      "w-full max-w-4xl",
                      "mb-6",
                      "px-3 sm:px-4"
                    )}
                  >
                    <PulsingLoader />
                  </div>
                );
              })()}

              {sortedMessages?.some((msg) =>
                shouldShowInStreamingMessage(msg)
              ) && (
                <div
                  ref={scrollRef}
                  className={BASE_CLASSES.anchor}
                  aria-hidden="true"
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Memoized component with smart comparison - allows streaming content updates
// CRITICAL: This comparison MUST allow re-renders when content changes during streaming
export const Messages = memo(PureMessages, (prevProps, nextProps) => {
  if (prevProps.id !== nextProps.id) return false;
  if (prevProps.className !== nextProps.className) return false;

  const prevMessages = prevProps.messages || [];
  const nextMessages = nextProps.messages || [];

  if (prevMessages.length !== nextMessages.length) return false;

  // CRITICAL: If messages array reference changed, always allow re-render
  // Store updates create new array references, so this ensures updates always trigger renders
  if (prevMessages !== nextMessages) {
    return false;
  }

  // If array reference is the same, check content changes (defensive check)
  // This handles edge cases where array reference might be reused

  // CRITICAL FIX: If there are any streaming messages, always allow re-renders
  // This ensures streaming content updates are never blocked
  const hasStreamingMessages = nextMessages.some(
    (msg) => msg?.isStreaming === true
  );
  if (hasStreamingMessages) {
    return false;
  }

  // Create maps for easier comparison by message ID (handles order changes)
  const prevMessageMap = new Map(prevMessages.map((msg) => [msg?.id, msg]));
  const nextMessageMap = new Map(nextMessages.map((msg) => [msg?.id, msg]));

  // CRITICAL: First check if ANY content has changed - this is the primary check
  // Do this BEFORE other checks to ensure content updates always trigger re-renders
  for (const [messageId, prev] of prevMessageMap) {
    const next = nextMessageMap.get(messageId);
    if (!next) continue; // Skip if message doesn't exist in next (handled below)

    // CRITICAL: Always allow re-render if content changes (even if isStreaming is false)
    // This ensures partial/incomplete responses display as they stream in
    // Content length change indicates streaming update
    const prevContent = prev?.content || "";
    const nextContent = next?.content || "";
    if (prevContent !== nextContent) {
      return false;
    }
  }

  // Now do full comparison for other changes
  for (const [messageId, prev] of prevMessageMap) {
    const next = nextMessageMap.get(messageId);

    // If message ID doesn't exist in next, it's a change
    if (!next) return false;

    // Check streaming state changes - CRITICAL for final update when streaming completes
    if (prev?.isStreaming !== next?.isStreaming) {
      return false;
    }

    // Check error state changes (Claude pattern - error messages need re-render)
    if (prev?.isError !== next?.isError) {
      return false;
    }

    // Check if file metadata changed (but files should remain stable during content updates)
    if (prev?.metadata?.storage_files !== next?.metadata?.storage_files) {
      return false;
    }

    // Check for tool calls changes
    const prevToolCalls = prev?.tool_calls || [];
    const nextToolCalls = next?.tool_calls || [];
    if (prevToolCalls.length !== nextToolCalls.length) {
      return false;
    }

    // Check metadata changes (but not storage_files which is handled above)
    // Use shallow comparison for metadata to catch updates like reasoningContent
    const prevMetadata = prev?.metadata || {};
    const nextMetadata = next?.metadata || {};
    const prevMetaKeys = Object.keys(prevMetadata).filter(
      (k) => k !== "storage_files"
    );
    const nextMetaKeys = Object.keys(nextMetadata).filter(
      (k) => k !== "storage_files"
    );

    if (prevMetaKeys.length !== nextMetaKeys.length) {
      return false;
    }

    // Check each metadata key (except storage_files)
    for (const key of prevMetaKeys) {
      if (prevMetadata[key] !== nextMetadata[key]) {
        // Allow re-render for metadata changes (like reasoningContent updates)
        return false;
      }
    }
  }

  // All checks passed - messages are the same (skip re-render)
  // This only happens when nothing meaningful has changed AND no streaming messages
  return true;
});

Messages.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  messages: PropTypes.array,
};

Messages.displayName = "Messages";

export default Messages;
